import { useEffect, useRef, useState } from 'react';
import { Sheet, SheetBody } from './Sheet';
import { openInstalledApp, useInstallPrompt } from '../installPrompt';
import { trackInstallPrompt, track } from '../telemetry';
import { createRestoreCode, hasProgress } from '../profileTransfer';

interface InstallSheetProps {
  where: string;
  headline?: string;
  // Let the moment land (result, confetti) before interrupting.
  delayMs?: number;
}

type Step = 'hidden' | 'offer' | 'ios-steps' | 'ios-done';

// Offers "add to home screen" in a popup, a beat after it mounts. Android gets
// the one-tap system prompt; iOS has no API, so it shows the Share → Add to
// Home Screen steps. Tapping outside just closes it; only "Not now" hides it
// for 14 days.
export function InstallSheet({ where, headline = 'Get the BrainFart app', delayMs = 1200 }: InstallSheetProps) {
  const { platform, dismiss } = useInstallPrompt();
  const [step, setStep] = useState<Step>('hidden');
  const shown = useRef(false);

  useEffect(() => {
    if (!platform || shown.current) return;
    const t = setTimeout(() => {
      shown.current = true;
      setStep('offer');
      trackInstallPrompt('shown', platform, where);
    }, delayMs);
    return () => clearTimeout(t);
  }, [platform, where, delayMs]);

  const notNow = () => {
    if (platform) trackInstallPrompt('dismissed', platform, where);
    dismiss();
    setStep('hidden');
  };

  return <InstallFlow step={step} setStep={setStep} where={where} headline={headline} onNotNow={notNow} />;
}

// Always-available entry point (homepage): shows whenever this device can
// install, even after "Not now".
export function InstallButton({ where, variant = 'button' }: { where: string; variant?: 'button' | 'link' }) {
  const { available, install } = useInstallPrompt();
  const [step, setStep] = useState<Step>('hidden');
  if (!available) return null;

  const onClick = async () => {
    trackInstallPrompt('clicked', available, where);
    if (available === 'ios') setStep('ios-steps');
    else await install(); // success is shown by <InstallSuccess />
  };

  return (
    <>
      {variant === 'link' ? (
        <button className="text-link" onClick={onClick}>Install the app</button>
      ) : (
        <button className="btn btn-ghost" onClick={onClick} style={{ alignSelf: 'center', padding: '0.6rem 1.25rem', fontSize: '0.9rem' }}>
          📲 Install the app
        </button>
      )}
      <InstallFlow step={step} setStep={setStep} where={where} />
    </>
  );
}

function InstallFlow({ step, setStep, where, headline = 'Get the BrainFart app', onNotNow }: {
  step: Step;
  setStep: (s: Step) => void;
  where: string;
  headline?: string;
  onNotNow?: () => void;
}) {
  const { available, install } = useInstallPrompt();
  const close = () => setStep('hidden');
  const restore = useRestoreCode(step === 'ios-steps' || step === 'ios-done');

  const onInstall = async () => {
    if (!available) return;
    trackInstallPrompt('clicked', available, where);
    if (available === 'ios') {
      setStep('ios-steps');
      return;
    }
    setStep('hidden');
    await install(); // success is shown by <InstallSuccess /> when the browser confirms
  };

  return (
    <Sheet open={step !== 'hidden'} onClose={close} label="Install BrainFart">
      {step === 'offer' && (
        <SheetBody
          icon={<img src="/icon-192.png" alt="" width={72} height={72} style={{ borderRadius: 18 }} />}
          title={headline}
          body="One tap to today's challenge from your home screen. Full screen, no browser bars, loads instantly."
        >
          <button className="btn btn-primary" onClick={onInstall}>
            📲 {available === 'ios' ? 'Add to Home Screen' : 'Install app'}
          </button>
          <button className="btn btn-ghost" onClick={onNotNow ?? close}>Not now</button>
        </SheetBody>
      )}

      {step === 'ios-steps' && (
        <SheetBody icon="📲" title="Two taps to add it">
          <ol style={{ textAlign: 'left', color: '#ddd', fontSize: '0.95rem', lineHeight: 1.7, margin: '0 0 1.25rem 1.25rem' }}>
            <li>Tap <strong>Share</strong> <span aria-hidden>(the square with an arrow)</span> in Safari's toolbar</li>
            <li>Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong></li>
            {restore.code && <li>Open BrainFart from your home screen. It asks for a restore code once, so your progress comes with you:</li>}
          </ol>
          {restore.code && <RestoreCodeBox code={restore.code} />}
          <button className="btn btn-primary" onClick={() => setStep('ios-done')}>I've added it</button>
          <button className="btn btn-ghost" onClick={close}>Close</button>
        </SheetBody>
      )}

      {step === 'ios-done' && (
        <SheetBody
          icon="🎉"
          title="You're set!"
          body={restore.code
            ? 'Open BrainFart from your home screen. It will ask for your restore code once, so your streak and ratings come along.'
            : 'Open BrainFart from your home screen and it launches full screen, straight into the game.'}
        >
          {restore.code && <RestoreCodeBox code={restore.code} />}
          <button className="btn btn-primary" onClick={close}>Got it</button>
        </SheetBody>
      )}
    </Sheet>
  );
}

// iOS: the home-screen app starts empty, so if there is progress worth keeping,
// make a one-time restore code while the install steps are open. The code is
// made once per opening; nothing is uploaded unless they get this far.
function useRestoreCode(active: boolean): { code: string | null } {
  const [code, setCode] = useState<string | null>(null);
  const started = useRef(false);
  useEffect(() => {
    if (!active || started.current || !hasProgress()) return;
    started.current = true;
    createRestoreCode().then(c => {
      if (c) track({ type: 'restore_code_shown', payload: {} });
      setCode(c);
    });
  }, [active]);
  return { code };
}

function RestoreCodeBox({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2_000);
    } catch { /* the code is on screen; they can type it */ }
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', margin: '0 0 1.25rem', padding: '0.7rem 0.9rem', borderRadius: 14, background: '#100E22', border: '1px solid #2E2B45' }}>
      <span aria-label="Restore code" style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: '1.5rem', fontWeight: 800, letterSpacing: '0.12em', color: '#FFC93C' }}>{code}</span>
      <button className="btn btn-ghost" onClick={copy} style={{ width: 'auto', padding: '0.5rem 0.9rem', fontSize: '0.9rem' }}>{copied ? 'Copied ✓' : 'Copy'}</button>
    </div>
  );
}

// Mounted once at the root: celebrates an install however it happened (our
// popup or the browser's own menu) and offers to open the app.
export function InstallSuccess() {
  const { justInstalled, clearJustInstalled } = useInstallPrompt();
  return (
    <Sheet open={justInstalled} onClose={clearJustInstalled} label="BrainFart installed">
      <SheetBody
        icon="🎉"
        title="BrainFart is installed!"
        body="It's on your home screen now. Open it for the full-screen version."
      >
        <button className="btn btn-primary" onClick={() => { openInstalledApp(); clearJustInstalled(); }}>
          🧠 Open BrainFart
        </button>
        <button className="btn btn-ghost" onClick={clearJustInstalled}>Keep playing here</button>
      </SheetBody>
    </Sheet>
  );
}
